"use client";
import React, { useEffect, useRef } from "react";
import Portal from "@rc-component/portal";
import { createStyles } from "antd-style";
import { ospThemes } from "@ospweb/sdk-ui";
import { SwapModalPlacement, SwapModalProps } from "./typs";

export const SwapModal: React.FC<SwapModalProps> = ({
  visible,
  onClose,
  content,
  children,
  placement = "center",
  maskClosable = true,
  closeOnEsc = true,
  className,
  style,
  afterClose,
  getContainer,
  mask = true,
  maskStyle,
  maskNode,
  animationDuration = 300,
  width,
  height,
  zIndex = 1000,
  maskCloseIcon,
  maskCloseIconClassName,
}) => {
  const { styles, cx } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);

  // 处理ESC键关闭
  useEffect(() => {
    if (!closeOnEsc || !visible) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeOnEsc, visible, onClose]);

  // 处理动画结束后的回调
  useEffect(() => {
    if (!visible && afterClose) {
      const timer = setTimeout(() => {
        afterClose();
      }, animationDuration);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [visible, afterClose, animationDuration]);

  // 根据位置获取对应的样式类名
  const getPlacementClassName = () => {
    switch (placement) {
      case "center":
        return styles.modalCenter;
      case "top":
        return styles.modalTop;
      case "bottom":
        return styles.modalBottom;
      case "left":
        return styles.modalLeft;
      case "right":
        return styles.modalRight;
      default:
        return styles.modalCenter;
    }
  };

  // 构建容器样式
  const containerStyle: React.CSSProperties = {
    ...style,
    transitionDuration: `${animationDuration}ms`,
    zIndex,
  };

  // 根据位置设置宽高
  if (
    width &&
    (placement === "center" || placement === "top" || placement === "bottom")
  ) {
    containerStyle.width = width;
  }

  if (
    height &&
    (placement === "center" || placement === "left" || placement === "right")
  ) {
    containerStyle.height = height;
  }

  // // 如果是左右位置，设置默认宽度
  // if ((placement === 'left' || placement === 'right') && !width) {
  //   containerStyle.width = '320px';
  // }

  // // 如果是上下位置，设置默认高度
  // if ((placement === 'top' || placement === 'bottom') && !height) {
  //   containerStyle.height = '320px';
  // }

  // 渲染模态框内容
  const renderContent = () => {
    // 如果有children，直接渲染children
    if (children) {
      return children;
    }

    // 否则渲染标题和内容
    return <>{content}</>;
  };

  return (
    <Portal getContainer={getContainer} open={visible}>
      {/* 遮罩层 */}
      {mask && (
        <div
          data-openreplay-label={`swap · SwapModal · div | ${maskNode}`}
          ref={maskRef}
          className={cx(styles.modalMask, { visible })}
          onClick={
            maskCloseIcon ? undefined : maskClosable ? onClose : undefined
          }
          style={{
            ...maskStyle,
            zIndex,
            transitionDuration: `${animationDuration}ms`,
          }}
        >
          {maskNode}
        </div>
      )}

      {/* 模态框容器 */}
      <div
        ref={containerRef}
        className={cx(
          styles.modalContainer,
          getPlacementClassName(),
          { visible },
          className,
        )}
        style={containerStyle}
      >
        {renderContent()}
      </div>
      {/* 遮罩关闭图标 置于模态框容器之上 避免被模态框遮挡 */}
      {mask && (
        <div
          data-openreplay-label={`swap · SwapModal · div | ${maskCloseIcon}`}
          className={cx(
            "absolute w-fit h-fit cursor-pointer z-1100",
            maskCloseIconClassName,
          )}
          onClick={maskCloseIcon ? onClose : undefined}
        >
          {maskCloseIcon}
        </div>
      )}
    </Portal>
  );
};

const useStyles = createStyles(({ css }) => {
  // 为不同位置创建样式
  const getPlacementStyles = (placement: SwapModalPlacement) => {
    //   const baseTransition = 'transform 0.3s ease-out, opacity 0.3s ease-out';

    switch (placement) {
      case "center":
        return css`
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) scale(0.8);
          opacity: 0;
          &.visible {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        `;
      case "top":
        return css`
          top: 0;
          left: 0;
          right: 0;
          transform: translateY(-100%);
          opacity: 0;
          &.visible {
            transform: translateY(0);
            opacity: 1;
          }
        `;
      case "bottom":
        return css`
          bottom: 0;
          left: 0;
          right: 0;
          transform: translateY(100%);
          opacity: 0;
          &.visible {
            transform: translateY(0);
            opacity: 1;
          }
        `;
      case "left":
        return css`
          top: 0;
          left: 0;
          bottom: 0;
          transform: translateX(-100%);
          opacity: 0;
          &.visible {
            transform: translateX(0);
            opacity: 1;
          }
        `;
      case "right":
        return css`
          top: 0;
          right: 0;
          bottom: 0;
          transform: translateX(100%);
          opacity: 0;
          &.visible {
            transform: translateX(0);
            opacity: 1;
          }
        `;
      default:
        return "";
    }
  };

  return {
    // 遮罩层样式
    modalMask: css`
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 1000;
      opacity: 0;
      transition: opacity 0.3s;
      &.visible {
        opacity: 1;
      }
    `,

    // 模态框容器基础样式
    modalContainer: css`
      position: fixed;
      z-index: 1001;
      background-color: ${ospThemes.colors.background_solid_secondary};
      border-radius: 16px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transition: all 0.3s;
    `,

    // 各个位置的样式
    modalCenter: getPlacementStyles("center"),
    modalTop: getPlacementStyles("top"),
    modalBottom: getPlacementStyles("bottom"),
    modalLeft: getPlacementStyles("left"),
    modalRight: getPlacementStyles("right"),
  };
});
